import React, { useState, useEffect } from "react";
import {
  getReferrals, createReferral, deleteReferral,
  getOrdersByReferralCode, getUsers,
  getReferralWithdrawals, updateReferralWithdrawal,
} from "../../firebase/firestoreService";
import { MOBILE_OPERATORS } from "../../services/paychangu";

const fmt     = (n)  => `MK ${Number(n || 0).toLocaleString()}`;
const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const STATUS_STYLE = {
  Pending:   { background: "#fff3e0", color: "#f57c00" },
  Confirmed: { background: "#e3f2fd", color: "#1565c0" },
  Shipped:   { background: "#f3e5f5", color: "#7b1fa2" },
  Delivered: { background: "#e8f5e9", color: "#2e7d32" },
  Cancelled: { background: "#fde8e8", color: "#c0392b" },
};

function CodesTab() {
  const [referrals,         setReferrals]         = useState([]);
  const [users,             setUsers]             = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [formOpen,          setFormOpen]          = useState(false);
  const [form,              setForm]              = useState({ label: "", code: "", commissionRate: "10", userId: "" });
  const [saving,            setSaving]            = useState(false);
  const [error,             setError]             = useState("");
  const [copied,            setCopied]            = useState(null);
  const [expandedCode,      setExpandedCode]      = useState(null);
  const [codeOrders,        setCodeOrders]        = useState({});
  const [codeOrdersLoading, setCodeOrdersLoading] = useState({});

  const load = async () => {
    setLoading(true);
    const data = await getReferrals();
    setReferrals(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    getUsers().then(setUsers);
  }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleCreate = async () => {
    const code  = form.code.trim().toUpperCase();
    const label = form.label.trim();
    const rate  = parseFloat(form.commissionRate);
    if (!label)                                { setError("Enter a name/label."); return; }
    if (!code)                                 { setError("Enter a referral code."); return; }
    if (isNaN(rate) || rate < 0 || rate > 100) { setError("Commission rate must be 0–100."); return; }
    if (referrals.some((r) => r.code === code)) { setError("That code already exists."); return; }
    setSaving(true);
    setError("");
    await createReferral({
      code,
      label,
      commissionRate: rate,
      userId:         form.userId || null,
      totalEarnings:  0,
      totalOrders:    0,
      totalSignups:   0,
      totalClicks:    0,
    });
    setForm({ label: "", code: "", commissionRate: "10", userId: "" });
    setFormOpen(false);
    await load();
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this referral code?")) return;
    await deleteReferral(id);
    await load();
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/?ref=${code}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const toggleExpand = async (code) => {
    if (expandedCode === code) { setExpandedCode(null); return; }
    setExpandedCode(code);
    if (!codeOrders[code]) {
      setCodeOrdersLoading((p) => ({ ...p, [code]: true }));
      const orders = await getOrdersByReferralCode(code);
      setCodeOrders((p) => ({ ...p, [code]: orders }));
      setCodeOrdersLoading((p) => ({ ...p, [code]: false }));
    }
  };

  const totalEarnings = referrals.reduce((s, r) => s + (r.totalEarnings || 0), 0);
  const totalOrders   = referrals.reduce((s, r) => s + (r.totalOrders   || 0), 0);
  const totalSignups  = referrals.reduce((s, r) => s + (r.totalSignups  || 0), 0);
  const totalClicks   = referrals.reduce((s, r) => s + (r.totalClicks   || 0), 0);

  const userLabel = (u) =>
    [u.firstName, u.lastName].filter(Boolean).join(" ") || u.phone || u.email || u.id;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <button className="btn-primary" onClick={() => { setFormOpen((v) => !v); setError(""); }}>
          {formOpen ? "Cancel" : "+ New Code"}
        </button>
      </div>

      {/* Summary stats */}
      <div className="stat-cards" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-card__label">Total Codes</div>
          <div className="stat-card__value">{referrals.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Total Clicks</div>
          <div className="stat-card__value">{totalClicks}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Total Signups</div>
          <div className="stat-card__value">{totalSignups}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Referred Orders</div>
          <div className="stat-card__value">{totalOrders}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Total Commissions</div>
          <div className="stat-card__value" style={{ fontSize: 18 }}>{fmt(totalEarnings)}</div>
        </div>
      </div>

      {/* Create form */}
      {formOpen && (
        <div className="referrals__form-card">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Name / Label</label>
              <input className="form-input" placeholder="e.g. John Doe"
                value={form.label} onChange={(e) => setF("label", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Referral Code</label>
              <input className="form-input" placeholder="e.g. JOHN25"
                value={form.code} onChange={(e) => setF("code", e.target.value.toUpperCase())} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Commission Rate (%)</label>
              <input className="form-input" type="number" min="0" max="100" placeholder="10"
                value={form.commissionRate} onChange={(e) => setF("commissionRate", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">
                Link to user account{" "}
                <span style={{ textTransform: "none", letterSpacing: 0, fontSize: 10, color: "#ccc" }}>
                  (optional — so they can see stats in their profile)
                </span>
              </label>
              <select
                className="form-input"
                value={form.userId}
                onChange={(e) => setF("userId", e.target.value)}
              >
                <option value="">— No account / skip —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{userLabel(u)}</option>
                ))}
              </select>
            </div>
          </div>
          {error && <p style={{ fontSize: 12, color: "#c0392b", marginTop: 6 }}>{error}</p>}
          <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
            <button className="btn-primary" onClick={handleCreate} disabled={saving}>
              {saving ? "Creating…" : "Create Code"}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="dash-table-wrap">
        <table className="dash-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Rate</th>
              <th>Clicks</th>
              <th>Signups</th>
              <th>Orders</th>
              <th>Earnings</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", color: "#aaa", padding: 32, fontSize: 13 }}>
                  Loading…
                </td>
              </tr>
            ) : referrals.length === 0 ? (
              <tr>
                <td colSpan={8} className="dash-empty">No referral codes yet.</td>
              </tr>
            ) : referrals.map((r) => (
              <React.Fragment key={r.id}>
                <tr>
                  <td style={{ fontWeight: 500 }}>{r.label}</td>
                  <td><span className="referrals__code-badge">{r.code}</span></td>
                  <td>{r.commissionRate}%</td>
                  <td>{r.totalClicks || 0}</td>
                  <td>{r.totalSignups || 0}</td>
                  <td>{r.totalOrders || 0}</td>
                  <td style={{ fontWeight: 600 }}>{fmt(r.totalEarnings)}</td>
                  <td>
                    <button
                      className="btn-edit"
                      onClick={() => toggleExpand(r.code)}
                      style={{ marginRight: 4 }}
                    >
                      {expandedCode === r.code ? "▲ Hide" : "▼ Orders"}
                    </button>
                    <button className="btn-edit" onClick={() => copyLink(r.code)}>
                      {copied === r.code ? "Copied!" : "Copy Link"}
                    </button>
                    <a
                      className="btn-edit"
                      href={`${window.location.origin}/?ref=${r.code}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none", display: "inline-block" }}
                    >
                      View Store →
                    </a>
                    <button className="btn-danger" onClick={() => handleDelete(r.id)}>Delete</button>
                  </td>
                </tr>

                {expandedCode === r.code && (
                  <tr>
                    <td colSpan={8} style={{ padding: 0, background: "#fafaf9" }}>
                      <div style={{ padding: "16px 20px" }}>
                        {codeOrdersLoading[r.code] ? (
                          <p style={{ fontSize: 13, color: "#aaa", margin: 0 }}>Loading orders…</p>
                        ) : !codeOrders[r.code]?.length ? (
                          <p style={{ fontSize: 13, color: "#aaa", margin: 0 }}>No orders for this code yet.</p>
                        ) : (
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>
                              <tr style={{ borderBottom: "1px solid #eee" }}>
                                <th style={{ textAlign: "left", padding: "6px 10px 6px 0", fontWeight: 600, color: "#777", fontSize: 11, letterSpacing: 1 }}>ORDER</th>
                                <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, color: "#777", fontSize: 11, letterSpacing: 1 }}>DATE</th>
                                <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, color: "#777", fontSize: 11, letterSpacing: 1 }}>CUSTOMER</th>
                                <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, color: "#777", fontSize: 11, letterSpacing: 1 }}>ITEMS</th>
                                <th style={{ textAlign: "right", padding: "6px 10px", fontWeight: 600, color: "#777", fontSize: 11, letterSpacing: 1 }}>TOTAL</th>
                                <th style={{ textAlign: "right", padding: "6px 10px", fontWeight: 600, color: "#777", fontSize: 11, letterSpacing: 1 }}>COMMISSION</th>
                                <th style={{ textAlign: "left", padding: "6px 0 6px 10px", fontWeight: 600, color: "#777", fontSize: 11, letterSpacing: 1 }}>STATUS</th>
                              </tr>
                            </thead>
                            <tbody>
                              {codeOrders[r.code].map((order) => {
                                const commission = order.commissionAmount ?? ((order.total || 0) * (r.commissionRate || 0)) / 100;
                                const itemsText  = (order.items || [])
                                  .map((item) => `${item.name}${(item.qty || 1) > 1 ? ` ×${item.qty}` : ""}`)
                                  .join(", ");
                                const st = STATUS_STYLE[order.status] || STATUS_STYLE.Pending;
                                return (
                                  <tr key={order.id} style={{ borderBottom: "1px solid #f5f2ee" }}>
                                    <td style={{ padding: "10px 10px 10px 0", fontWeight: 600 }}>
                                      #{(order.chargeId || order.id).slice(0, 8).toUpperCase()}
                                    </td>
                                    <td style={{ padding: "10px", color: "#777" }}>{fmtDate(order.createdAt)}</td>
                                    <td style={{ padding: "10px" }}>{order.customerName || "—"}</td>
                                    <td style={{ padding: "10px", color: "#555", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {itemsText || "—"}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right", fontWeight: 600 }}>{fmt(order.total)}</td>
                                    <td style={{ padding: "10px", textAlign: "right", fontWeight: 700, color: "#2e7d32" }}>+{fmt(commission)}</td>
                                    <td style={{ padding: "10px 0 10px 10px" }}>
                                      <span style={{ ...st, fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: "3px 8px", borderRadius: 20 }}>
                                        {order.status || "Pending"}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Payout Requests tab ──────────────────────────────────────────────────────
const WITHDRAWAL_STATUS_STYLE = {
  Pending:  { background: "#fff3e0", color: "#f57c00" },
  Paid:     { background: "#e8f5e9", color: "#2e7d32" },
  Rejected: { background: "#fde8e8", color: "#c0392b" },
};

function PayoutRequestsTab() {
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [acting,   setActing]   = useState(null);

  const load = async () => {
    setLoading(true);
    setRequests(await getReferralWithdrawals());
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setActing(id);
    await updateReferralWithdrawal(id, { status });
    await load();
    setActing(null);
  };

  const pending = requests.filter((r) => r.status === "Pending");

  return (
    <div>
      <p style={{ fontSize: 12, color: "#aaa", marginBottom: 20, lineHeight: 1.6 }}>
        Referral owners request payouts here; you send the money manually via their chosen mobile
        money number, then mark the request Paid.
      </p>

      <div className="stat-cards" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-card__label">Pending Requests</div>
          <div className="stat-card__value">{pending.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Pending Amount</div>
          <div className="stat-card__value" style={{ fontSize: 20 }}>
            {fmt(pending.reduce((s, r) => s + (r.amount || 0), 0))}
          </div>
        </div>
      </div>

      <div className="dash-table-wrap">
        <table className="dash-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Code</th>
              <th>Operator</th>
              <th>Number</th>
              <th>Amount</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", color: "#aaa", padding: 32, fontSize: 13 }}>
                  Loading…
                </td>
              </tr>
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={7} className="dash-empty">No payout requests yet.</td>
              </tr>
            ) : requests.map((r) => (
              <tr key={r.id}>
                <td>{fmtDate(r.createdAt)}</td>
                <td><span className="referrals__code-badge">{r.referralCode}</span></td>
                <td>{MOBILE_OPERATORS.find((o) => o.id === r.operator)?.label || r.operator}</td>
                <td>{r.phone}</td>
                <td style={{ fontWeight: 600 }}>{fmt(r.amount)}</td>
                <td>
                  <span style={{
                    ...(WITHDRAWAL_STATUS_STYLE[r.status] || WITHDRAWAL_STATUS_STYLE.Pending),
                    fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: "3px 8px", borderRadius: 20,
                  }}>
                    {r.status || "Pending"}
                  </span>
                </td>
                <td>
                  {r.status === "Pending" && (
                    <>
                      <button
                        className="btn-edit"
                        onClick={() => setStatus(r.id, "Paid")}
                        disabled={acting === r.id}
                      >
                        {acting === r.id ? "…" : "Mark Paid"}
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => setStatus(r.id, "Rejected")}
                        disabled={acting === r.id}
                      >
                        Reject
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────
export default function ReferralsSection() {
  const [tab, setTab] = useState("codes");

  return (
    <div>
      <div className="dash-main__header">
        <h2 className="dash-main__title">Referrals</h2>
        <div style={{ display: "flex", gap: 6, marginLeft: "auto", flexWrap: "wrap" }}>
          {[{ key: "codes", label: "Codes" }, { key: "requests", label: "Payout Requests" }].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: "5px 16px", fontSize: 11, fontWeight: 600,
                fontFamily: "inherit", borderRadius: 20, border: "1px solid", cursor: "pointer",
                transition: "all .15s",
                borderColor: tab === t.key ? "#1a1a1a" : "#ddd",
                background:  tab === t.key ? "#1a1a1a" : "#fff",
                color:       tab === t.key ? "#fff"    : "#888",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "codes" ? <CodesTab /> : <PayoutRequestsTab />}
    </div>
  );
}
